// New merge request… (spec/dashboard.md section 12.3): source (default the current branch), target (default the game's
// default branch when it differs), title, description → `POST …/merge-requests`; on success the request page opens; a `409`
// (the pair is open or already merged) is shown in the dialog as the server says it.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ops } from '../../api/operations';
import type { MergeRequest } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Select, TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { queryCache } from '../../workspace/cache';
import { asApiError, mrPrefix } from './merge';
import { LoadError } from '../../ui/PageStates';

export function NewMergeRequestDialog({ branch, onClose }: { branch: string; onClose: () => void }) {
  const { org, game, branches } = useGame();
  const { client } = useAuth();
  const navigate = useNavigate();
  const defaultBranch = branches.find((b) => b.isDefault)?.name;
  const [source, setSource] = useState(branch);
  const [target, setTarget] = useState(() => (defaultBranch && defaultBranch !== branch ? defaultBranch : (branches.find((b) => b.name !== branch)?.name ?? '')));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const same = source !== '' && source === target;
  const ready = source !== '' && target !== '' && !same && title.trim() !== '';
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = { sourceBranch: source, targetBranch: target, title: title.trim(), ...(description.trim() ? { description: description.trim() } : {}) };
      const mr = (await client.data(ops.createMergeRequest, { path: { org, game: game.slug }, body: body })) as unknown as MergeRequest;
      queryCache.invalidate(mrPrefix(org, game.slug));
      onClose();
      navigate(routes.mergeRequest(org, game.slug, branch, mr.number));
    } catch (e) {
      const err = asApiError(e);
      setError(err.message + (err.requestId ? ` (request ${err.requestId})` : ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="m"
      title="New merge request"
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!ready} busy={busy} title={same ? 'The source and the target must differ.' : !title.trim() ? 'A title is required.' : undefined}>
            Create
          </Button>
        </>
      }
    >
      <form
        data-testid="new-mr-dialog"
        className="mr-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="form-grid">
        <Field label="Source branch" error={same ? 'The source and the target must differ.' : undefined}>
          <Select aria-label="Source branch" value={source} onChange={(e) => setSource(e.target.value)}>
            {branches.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Target branch">
          <Select aria-label="Target branch" value={target} onChange={(e) => setTarget(e.target.value)}>
            {target === '' && <option value="">—</option>}
            {branches.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
                {b.isProtected ? ' (protected)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        </div>
        <Field label="Title" required>
          <TextInput aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus />
        </Field>
        <Field label="Description">
          <TextArea aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
        </Field>
        {error && (
          <LoadError>{error}</LoadError>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
